import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Building2,
  CalendarClock,
  ChevronDown,
  FileText,
  GalleryHorizontal,
  Image as ImageIcon,
  KeyRound,
  LayoutDashboard,
  LayoutTemplate,
  LineChart,
  LogOut,
  Mail,
  Newspaper,
  Settings,
  Sparkles,
} from "lucide-react";
import { Toaster } from "sonner";
import { adminApi, getAdminToken, setAdminToken } from "./api";

// Top-level standalone item (always visible above groups)
const HOME_ITEM = { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true };

// Grouped navigation
const NAV_GROUPS = [
  {
    id: "contenuti",
    label: "Contenuti",
    items: [
      { to: "/admin/pages", label: "Testi Pagine", icon: LayoutTemplate },
      { to: "/admin/hotels", label: "Hotel", icon: Building2 },
      { to: "/admin/openings", label: "Nuove Aperture", icon: CalendarClock },
      { to: "/admin/news", label: "News", icon: Newspaper },
      { to: "/admin/before-after", label: "Prima / Dopo", icon: Sparkles },
      { to: "/admin/marquee", label: "Loghi Marquee", icon: GalleryHorizontal },
      { to: "/admin/market", label: "Mercato Milano", icon: LineChart },
      { to: "/admin/media", label: "Media Library", icon: ImageIcon },
    ],
  },
  {
    id: "comunicazioni",
    label: "Comunicazioni",
    items: [
      { to: "/admin/messages", label: "Messaggi", icon: Mail },
      { to: "/admin/documents", label: "Documenti Investitori", icon: FileText },
      { to: "/admin/investor-sections", label: "Sezioni Investitori", icon: LayoutTemplate },
      { to: "/admin/access-codes", label: "Codici Investitori", icon: KeyRound },
    ],
  },
  {
    id: "sistema",
    label: "Sistema",
    items: [
      { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/admin/settings", label: "Impostazioni", icon: Settings },
    ],
  },
];

const STORAGE_KEY = "ih_admin_nav_open_v1";

function loadOpenState(currentPath) {
  // By default, open the group that contains the current path; remember user preference otherwise
  if (typeof window === "undefined") return {};
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (saved && typeof saved === "object") return saved;
  } catch {
    /* ignore corrupted state */
  }
  const init = {};
  NAV_GROUPS.forEach((g) => {
    init[g.id] = g.items.some((it) => currentPath.startsWith(it.to));
  });
  // First load: also open "Contenuti" by default — most-used section
  init.contenuti = true;
  return init;
}

export default function AdminLayout() {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [openGroups, setOpenGroups] = useState(() => loadOpenState(pathname));

  // Persist open/closed state of groups
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(openGroups));
    } catch {
      /* ignore */
    }
  }, [openGroups]);

  // When navigating, auto-open the group that contains the active route
  useEffect(() => {
    const grp = NAV_GROUPS.find((g) => g.items.some((it) => pathname.startsWith(it.to)));
    if (grp && !openGroups[grp.id]) {
      setOpenGroups((prev) => ({ ...prev, [grp.id]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const toggleGroup = (id) =>
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  useEffect(() => {
    const t = getAdminToken();
    if (!t) {
      setReady(true);
      return;
    }
    adminApi
      .get("/admin/me")
      .then((r) => setUser(r.data))
      .catch(() => setAdminToken(""))
      .finally(() => setReady(true));
  }, []);

  if (!ready) {
    return (
      <div className="min-h-screen bg-[#08080A] grid place-items-center text-white/40">
        Caricamento…
      </div>
    );
  }

  if (!user) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    setAdminToken("");
    navigate("/admin/login");
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
      isActive
        ? "bg-white/10 text-white"
        : "text-white/60 hover:text-white hover:bg-white/5"
    }`;

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors />
      <div className="min-h-screen bg-[#08080A] text-white grid grid-cols-[260px_1fr]">
        <aside className="border-r border-white/5 bg-[#0A0A0D] flex flex-col">
        <div className="px-6 py-7 border-b border-white/5">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/brand/invest-hospitality-logo.svg"
              alt="Invest Hospitality"
              className="h-8 w-auto"
            />
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#C48F56]">
              Admin · CMS
            </span>
          </Link>
        </div>

        <nav className="flex-1 py-5 px-3 overflow-y-auto">
          {/* Standalone Dashboard link */}
          <NavLink
            to={HOME_ITEM.to}
            end={HOME_ITEM.end}
            data-testid="admin-nav-dashboard"
            className={linkClass}
          >
            <HOME_ITEM.icon size={15} />
            {HOME_ITEM.label}
          </NavLink>

          {/* Collapsible groups */}
          {NAV_GROUPS.map((group) => {
            const isOpen = !!openGroups[group.id];
            const hasActive = group.items.some((it) => pathname.startsWith(it.to));
            return (
              <div key={group.id} className="mt-5">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  data-testid={`admin-nav-group-${group.id}`}
                  aria-expanded={isOpen}
                  className="flex items-center justify-between w-full px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-mono text-white/40 hover:text-white/70 transition-colors"
                >
                  <span className={hasActive ? "text-[#C48F56]" : ""}>
                    {group.label}
                  </span>
                  <ChevronDown
                    size={12}
                    className={`transition-transform ${isOpen ? "" : "-rotate-90"}`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-[max-height] duration-300 ease-out ${
                    isOpen ? "max-h-[400px]" : "max-h-0"
                  }`}
                >
                  <div className="space-y-1 pt-1">
                    {group.items.map((n) => (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        data-testid={`admin-nav-${n.to.split("/").pop()}`}
                        className={linkClass}
                      >
                        <n.icon size={15} />
                        {n.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        <div className="p-5 border-t border-white/5">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
            Connesso come
          </div>
          <div className="mt-1 text-sm truncate">{user.email}</div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs hover:bg-white/5 w-full justify-center"
          >
            <LogOut size={12} /> Esci
          </button>
        </div>
      </aside>

      <main className="overflow-auto">
        <div className="px-10 py-10 max-w-6xl">
          <Outlet />
        </div>
      </main>
      </div>
    </>
  );
}
